import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button, Spinner } from '../../../components/ui';

const CATEGORY_LABELS = {
  electronics: 'Electronics',
  furniture: 'Furniture',
  gaming: 'Gaming',
  fashion: 'Fashion',
  sports: 'Sports',
  collectibles: 'Collectibles',
  home: 'Home & Garden',
  toys: 'Toys',
  music: 'Music & Instruments',
  books: 'Books & Media',
  vehicles: 'Vehicles',
  other: 'Other',
};

const CONDITION_LABELS = {
  new: 'New',
  likeNew: 'Like New',
  good: 'Good',
  fair: 'Fair',
};

/**
 * Step 6: Review
 *
 * Summary of all raffle details before publishing.
 * No upfront fee - 5% platform fee on successful sales.
 */
export default function StepReview({ onNext, onBack, onSubmit }) {
  const { watch } = useFormContext();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const photos = watch('photos');
  const title = watch('title');
  const description = watch('description');
  const category = watch('category');
  const condition = watch('condition');
  const ticketPrice = watch('ticketPrice');
  const ticketQuantity = watch('ticketQuantity');
  const endsAt = watch('endsAt');
  const minimumThreshold = watch('minimumThreshold');
  const pickupArea = watch('pickupArea');
  const pickupAddress = watch('pickupAddress');

  const priceInEuros = ticketPrice ? (ticketPrice / 100).toFixed(2) : '0.00';
  const totalRevenue = ticketPrice && ticketQuantity
    ? (ticketPrice * ticketQuantity) / 100
    : 0;
  const platformFee = totalRevenue * 0.05; // 5% platform fee
  const sellerEarnings = totalRevenue - platformFee;

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-IE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const daysRemaining = endsAt
    ? Math.ceil((new Date(endsAt) - new Date()) / (1000 * 60 * 60 * 24))
    : 0;

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit?.();
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  }

  function SectionHeader({ title, step, onEdit }) {
    return (
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-cream">{title}</h3>
        <button
          type="button"
          onClick={onEdit}
          className="text-sm text-primary hover:text-primary-light transition-colors"
        >
          Edit
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-display text-cream mb-2">
          Review Your Competition
        </h1>
        <p className="text-cream/60">
          Double-check everything before publishing.
        </p>
      </div>

      {/* Review Sections */}
      <div className="space-y-6 mb-8">
        {/* Photos */}
        <div className="bg-midnight-light border border-midnight-lighter rounded-xl p-5">
          <SectionHeader title="Photos" step={0} onEdit={() => onBack?.(0)} />
          <div className="flex gap-2 overflow-x-auto pb-2">
            {photos?.map((url, index) => (
              <div key={url} className="relative flex-shrink-0">
                <img
                  src={url}
                  alt={`Photo ${index + 1}`}
                  className="w-20 h-20 object-cover rounded-lg"
                />
                {index === 0 && (
                  <span className="absolute bottom-1 left-1 text-[10px] bg-primary px-1.5 py-0.5 rounded text-midnight font-medium">
                    Cover
                  </span>
                )}
              </div>
            ))}
          </div>
          <p className="text-cream/50 text-sm mt-2">{photos?.length || 0} photo(s)</p>
        </div>

        {/* Details */}
        <div className="bg-midnight-light border border-midnight-lighter rounded-xl p-5">
          <SectionHeader title="Details" step={1} onEdit={() => onBack?.(1)} />
          <div className="space-y-3">
            <div>
              <span className="text-cream/50 text-sm">Title</span>
              <p className="text-cream">{title}</p>
            </div>
            <div>
              <span className="text-cream/50 text-sm">Description</span>
              <p className="text-cream text-sm line-clamp-3">{description}</p>
            </div>
            <div className="flex gap-6">
              <div>
                <span className="text-cream/50 text-sm">Category</span>
                <p className="text-cream">{CATEGORY_LABELS[category] || category}</p>
              </div>
              <div>
                <span className="text-cream/50 text-sm">Condition</span>
                <p className="text-cream">{CONDITION_LABELS[condition] || condition}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-midnight-light border border-midnight-lighter rounded-xl p-5">
          <SectionHeader title="Pricing" step={2} onEdit={() => onBack?.(2)} />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-cream/50 text-sm">Entry Price</span>
              <p className="text-cream text-lg font-semibold">€{priceInEuros}</p>
            </div>
            <div>
              <span className="text-cream/50 text-sm">Total Entries</span>
              <p className="text-cream text-lg font-semibold">{ticketQuantity}</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-midnight-lighter space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-cream/60">If all entries sell</span>
              <span className="text-cream/60">€{totalRevenue.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-cream/60">Platform fee (5%)</span>
              <span className="text-cream/60">-€{platformFee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-cream font-medium">You'll receive</span>
              <span className="text-primary font-semibold">€{sellerEarnings.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Schedule */}
        <div className="bg-midnight-light border border-midnight-lighter rounded-xl p-5">
          <SectionHeader title="Schedule" step={3} onEdit={() => onBack?.(3)} />
          <div className="space-y-3">
            <div>
              <span className="text-cream/50 text-sm">Ends</span>
              <p className="text-cream">{formatDate(endsAt)}</p>
              <p className="text-cream/50 text-sm">{daysRemaining} day(s) from now</p>
            </div>
            <div>
              <span className="text-cream/50 text-sm">Minimum Threshold</span>
              <p className="text-cream">{minimumThreshold}% of entries must sell</p>
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="bg-midnight-light border border-midnight-lighter rounded-xl p-5">
          <SectionHeader title="Location" step={4} onEdit={() => onBack?.(4)} />
          <div className="space-y-3">
            <div>
              <span className="text-cream/50 text-sm">Public Area</span>
              <p className="text-cream">{pickupArea}</p>
            </div>
            <div>
              <span className="text-cream/50 text-sm">Full Address (hidden until won)</span>
              <p className="text-cream text-sm">{pickupAddress}</p>
            </div>
          </div>
        </div>

        {/* Platform Fee Notice */}
        <div className="bg-primary/10 border border-primary/30 rounded-xl p-5">
          <h3 className="font-semibold text-cream mb-2 flex items-center gap-2">
            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Free to List
          </h3>
          <p className="text-cream/60 text-sm">
            Your competition goes live immediately. We only take a 5% fee when your competition
            succeeds - this covers payment processing, escrow protection, and dispute handling.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-danger/10 border border-danger/30 rounded-xl p-4">
            <p className="text-danger text-sm">{error}</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between gap-4">
        <Button variant="outline" onClick={() => onBack?.(4)} disabled={submitting}>
          Back
        </Button>
        <Button onClick={handleSubmit} disabled={submitting}>
          {submitting ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Publishing...
            </>
          ) : (
            'Publish Competition'
          )}
        </Button>
      </div>
    </div>
  );
}
